<template>
  <div v-if="test" class="container">

    <section class="test-header">
      <div class="test-header__lead">{{ $t('lead_test_for') }}</div>
      <h1 class="test-header__title" v-if="lesson">«{{ lesson.title }}»</h1>
      <h2 class="test-header__subtitle" v-if="course"><span class="test-header__label">{{ $t('course_label') }}:</span> {{ course.title }}</h2>
    </section>

    <section class="test-questions">
      <div class="test-questions__header">
        <label class="test-questions__counter">{{ $t('question') }} {{ currentQuestionKey + 1 }}/{{ questionsCount }}</label>
        <ul class="test-questions__dots dots">
          <li class="dots__dot"
               v-for="(question, key) in questions"
               :key="key"
               :class="{ 'dots__dot--active' : key === currentQuestionKey }"></li>
        </ul>
      </div>
      <div class="test-questions__wrapper" v-if="questions && variants">
        <article class="test-questions__question question"
                 v-for="(question, key) in questions"
                 :key="key"
                 v-if="currentQuestionKey === key">
          <h2 class="question__title">{{ question.title }}</h2>
          <div class="question__variants" v-if="answers[question.uuid] && variants">
            <checkbox v-model="answers[question.uuid][variant.uuid]"
                      class="question__variant"
                      :key="key"
                      v-for="(variant, key) in variants[question.uuid]">{{ variant.text }}</checkbox>
          </div>
        </article>
      </div>
      <div class="test-questions__controls">
        <button class="test-questions__control button button--small button--small-rounded button--white-blue"
                v-if="currentQuestionKey > 0"
                @click.prevent="currentQuestionKey--">{{ $t('previous_question') }}</button>
        <button class="test-questions__control button button--small button--small-rounded button--blue"
                v-if="currentQuestionKey < questionsCount - 1"
                @click.prevent="currentQuestionKey++">{{ $t('next_question') }}</button>
        <button class="test-questions__control button button--small button--small-rounded button--blue"
                v-if="currentQuestionKey == questionsCount - 1"
                :disabled="answersForm.busy"
                @click.prevent="checkTest()">{{ $t('check_test') }}</button>
      </div>
    </section>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { Form } from 'vform'
  import swal from 'sweetalert2'
  import { i18n } from '~/plugins/i18n'
  import VariantsService from '~/services/variants';

  export default {
    metaInfo () {
      return { title: this.test ? this.test.title : '' }
    },
    components: {
    },
    data: () => ({
      currentQuestionKey: 0,
      questionsCount: 0,
      answers: {},
      answersForm: new Form({
        answers: {},
        uuid: ''
      }),
      variants: []
    }),
    computed: mapGetters({
      user: 'auth/user',
      course: 'courses/course',
      lesson: 'lessons/lesson',
      test: 'tests/test',
      questions: 'questions/questions',
    }),
    methods: {
      async checkTest () {
        const preparedAnswers = {};

        for (const questionId in this.answers) {
          if (this.answers.hasOwnProperty(questionId)) {
            preparedAnswers[questionId] = {};

            for (const variantId in this.answers[questionId]) {
              if (this.answers[questionId].hasOwnProperty(variantId) && this.answers[questionId][variantId] === true) {
                preparedAnswers[questionId][variantId] = true;
              }
            }
          }
        }

        this.answersForm.answers = preparedAnswers;
        this.answersForm.uuid = this.test.uuid;

        const { data } = await this.answersForm.post('/api/user-test-attempts');

        const testForm = new Form({
          uuid: data
        });
        const response = await testForm.post('/api/check-attempt');
        const passed = response.data.passed;
        const result = response.data.result;

        let nextLessonId = (this.$route.params.lessonId ? this.$route.params.lessonId : 1) + 1;
        const nextLessonExist = false;
        // this.course.lessons.forEach(lesson => {

          // if (parseInt(lesson.innerId) === parseInt(nextLessonId)) {
          //   next_lesson_exist = true;
            // return lesson;
          // }
        // });
        if (!nextLessonExist) {
          nextLessonId = 0;
        }

        if (passed) {
          swal({
            type: 'success',
            title: i18n.t('test_passed_alert_title'),
            html: `
            <h3>${i18n.t('test_passed_alert_text', { result: result, max: this.questions.length, percent: Math.round(result * 100 / this.questions.length) })}</h3>
            ${i18n.t('test_passed_cert_sent')}
            `,
            reverseButtons: true,
            showCancelButton: nextLessonExist,
            confirmButtonText: i18n.t(nextLessonExist ? 'next_lesson' : 'finish_course'),
            cancelButtonText: i18n.t('nah_im_fine')
          }).then((result) => {
            this.$store.dispatch('auth/fetchUser');

            if (nextLessonExist) {
              if (this.user.user_lessons.find(el => el.id === nextLessonId) === undefined) {
                this.$store.dispatch('lessons/study', { id: nextLessonId });
              }

              this.$router.push({
                name: 'lesson.view',
                params: {
                  courseId: this.$route.params.courseId ? this.$route.params.courseId : 4,
                  id: nextLessonId
                },
              });
            } else {
              this.$router.push({ name: 'home' });
            }
          }, (cancel) => {
            console.log(cancel);
            this.$store.dispatch('auth/fetchUser');
            this.$router.push({ name: 'home' });
          });
        } else {
          swal({
            type: 'warning',
            title: i18n.t('test_failed_alert_title'),
            html: `<h3>${i18n.t('test_failed_alert_text', { result: result, max: this.questions.length, percent: Math.round(result * 100 / this.questions.length) })}</h3>`,
            reverseButtons: true,
            showCancelButton: true,
            confirmButtonText: i18n.t('study_again'),
            cancelButtonText: i18n.t('nah_im_fine')
          }).then((result) => {
            this.$store.dispatch('auth/fetchUser');

            this.$router.push({ name: 'lesson.view', params: {
              courseId: this.$route.params.courseId ? this.$route.params.courseId : 4,
              id: this.$route.params.lessonId ? this.$route.params.lessonId : 1,
            }});
          }, (cancel) => {
            this.$store.dispatch('auth/fetchUser');
            this.$router.push({ name: 'home' });
          });
        }
      },
    },
    async beforeMount () {
      const courseId = this.$route.params.courseId;
      const lessonId = this.$route.params.lessonId;
      const testId = this.$route.params.id;

      const promise = Promise.all([
        this.$store.dispatch('courses/find', courseId),
        this.$store.dispatch('lessons/find', { courseId, lessonId }),
        this.$store.dispatch('tests/find', testId),
        this.$store.dispatch('questions/fetchTestQuestions', testId),
      ]);

      promise.then(() => {
        this.$store.dispatch('pages/stopLoading');
        this.$store.dispatch('pages/setBreadcrumbs', [
          {
            title: 'Главная',
            route: { name: 'home' }
          },
          {
            title: `Курс: "${this.course.title}"`,
            route: { name: 'course.view', params: { id: courseId }},
          },
          {
            title: `Занятие: "${this.lesson.title}"`,
            route: { name: 'lesson.view', params: { id: lessonId, courseId: courseId }},
          },
          {
            title: 'Тест',
          },
        ]);

        const promises = [];
        for (const question of this.questions) {
          this.answers[question.uuid] = {};
          promises.push(VariantsService.getVariants(question.uuid));
        }
        Promise.all(promises).then(allVariants => {
          for (let i = 0; i < this.questions.length; i++) {
            this.variants[this.questions[i].uuid] = allVariants[i];
          }
          this.questionsCount = this.questions.length;
        });
      });
    }
  }
</script>
